import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { auth as authApi } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Check, ArrowRight, Lock, ShieldCheck } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";
import BrandedLoader from "@/components/BrandedLoader";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import SEO from "@/components/SEO";
import useAppReady from "@/lib/useAppReady";
import { getRandomQuote, parseHeadline } from "@/lib/quotes";

const LOGO     = "/assets/images/kiddo-logo.png";
const WORDMARK = "/assets/images/kiddo-wordmark.png";

export default function Login() {
  useAppReady();

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const nextPath = params.get("next");
  const disaMessage = location.state?.disaMessage;

  const quote = useMemo(() => getRandomQuote(), []);

  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Fade-in after logout overlay clears, with fallback for direct navigation.
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const onReveal = () => setRevealed(true);
    window.addEventListener("kpp:login:reveal", onReveal);
    const fallback = setTimeout(() => setRevealed(true), 160);
    return () => {
      window.removeEventListener("kpp:login:reveal", onReveal);
      clearTimeout(fallback);
    };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, pwd);
      toast.success("Welcome back!");
      if (nextPath && nextPath.startsWith("/")) navigate(nextPath, { replace: true });
      else if (res.user.role === "admin") navigate("/admin", { replace: true });
      else navigate("/activities", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Log in — Kiddo+"
        description="Sign in to Kiddo+ to continue your child's developmental journey."
        path="/login"
        noindex
      />
      <BrandedLoader visible={loading} title="Logging you in…" subtitle="Just a moment while we open the door." />

      {/* ── MOBILE — quote-hero top + white bottom-sheet card ─── */}
      <div
        className={`md:hidden min-h-[100dvh] flex flex-col transition-opacity duration-300 bg-[#EBF5F2] dark:bg-[#091714] ${revealed ? "opacity-100" : "opacity-0"}`}
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
        data-testid="mobile-auth-layout"
        data-kp-auth-page="true"
      >
        {/* Quote section */}
        <div className="flex-shrink-0 px-7 pt-14 pb-10 flex flex-col items-center text-center">
          <span
            className="block leading-none mb-3 select-none text-[#2A7B6F] dark:text-teal-400"
            style={{ fontSize: "3.4rem", fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 0.85 }}
          >❝</span>

          <h1 className="text-[1.55rem] font-black leading-snug tracking-tight text-[#1A2E2A] dark:text-[#DFF2EE]">
            {quote.headline.split("\n").map((line, li, arr) => (
              <span key={li}>
                {parseHeadline(line).map((part, pi) =>
                  part.teal
                    ? <span key={pi} className="text-[#2A7B6F] dark:text-teal-400">{part.text}</span>
                    : part.text
                )}
                {li < arr.length - 1 && <br />}
              </span>
            ))}
          </h1>

          <div className="my-4 rounded-full bg-[#2A7B6F] dark:bg-teal-400" style={{ width: 38, height: 2.5 }} />

          {(() => {
            const parts = quote.tagline.split("\n");
            return parts.length >= 2 ? (
              <>
                <p className="text-sm leading-relaxed text-[#4A5568] dark:text-gray-400">{parts[0]}</p>
                <p className="text-sm font-medium text-[#2A7B6F] dark:text-teal-400">{parts[1]}</p>
              </>
            ) : (
              <p className="text-sm font-medium text-[#2A7B6F] dark:text-teal-400">{parts[0]}</p>
            );
          })()}
        </div>

        {/* White bottom-sheet card */}
        <div
          className="bg-white dark:bg-kp-surface flex-1 overflow-y-auto"
          style={{ borderRadius: "2rem 2rem 0 0", boxShadow: "0 -4px 32px rgba(0,0,0,0.08)" }}
        >
          <div className="flex justify-center pt-3 pb-1">
            <div className="rounded-full bg-gray-300 dark:bg-gray-600" style={{ width: 36, height: 4 }} />
          </div>

          <div className="px-6 pt-5 pb-10">
            {/* DISA banner */}
            {disaMessage && (
              <div
                className="mb-5 flex items-start gap-2.5 p-3 rounded-2xl text-sm bg-[#EBF5F2] dark:bg-teal-950/40 border border-[#B2D8D0] dark:border-teal-700/40 text-[#1A5C52] dark:text-teal-300"
                data-testid="disa-login-message"
              >
                <span className="mt-0.5 text-[#2A7B6F] dark:text-teal-400">✦</span>
                <span>
                  {disaMessage}{" "}
                  <Link to="/signup" className="font-bold underline">Create a free account</Link>{" "}
                  or log in below.
                </span>
              </div>
            )}

            {/* Google button */}
            <GoogleSignInButton
              label="Continue with Google"
              className="!rounded-full !py-[1.7vh] mb-5 !bg-[#ECEDF8] dark:!bg-[#1A2535]"
              style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.09), 0 1px 2px rgba(0,0,0,0.06)" }}
            />

            {/* "or" divider */}
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
              <span className="text-sm text-gray-400 dark:text-gray-500">or</span>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            </div>

            {/* Form */}
            <form onSubmit={onSubmit} className="flex flex-col gap-5">
              {/* Email */}
              <div>
                <Label htmlFor="m-email" className="block text-sm font-bold mb-2 text-[#1A2E2A] dark:text-[#DFF2EE]">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" style={{ width: 18, height: 18 }} />
                  <Input
                    id="m-email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-auto text-sm placeholder:text-gray-400 focus-visible:ring-offset-0"
                    style={{ borderRadius: "9999px", paddingTop: "0.85rem", paddingBottom: "0.85rem", paddingLeft: "3rem", paddingRight: "1rem" }}
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="m-pwd" className="text-sm font-bold text-[#1A2E2A] dark:text-[#DFF2EE]">Password</Label>
                  <button
                    type="button"
                    onClick={() => setForgotOpen(true)}
                    className="text-sm font-medium text-[#2A7B6F] dark:text-teal-400"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10 text-gray-400" style={{ width: 18, height: 18 }} />
                  <PasswordInput
                    id="m-pwd"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="h-auto text-sm placeholder:text-gray-400 focus-visible:ring-offset-0"
                    style={{ borderRadius: "9999px", paddingTop: "0.85rem", paddingBottom: "0.85rem", paddingLeft: "3rem" }}
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              {/* Remember me */}
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-5 h-5 rounded cursor-pointer"
                  style={{ accentColor: "#2A7B6F" }}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">Remember me</span>
              </label>

              {/* Log in button */}
              <Button
                type="submit"
                disabled={loading}
                className="relative h-auto w-full text-white rounded-full font-bold text-base transition-colors"
                style={{ background: loading ? "#4A9E8F" : "#2A7B6F", paddingTop: "0.9rem", paddingBottom: "0.9rem" }}
                data-testid="login-submit-btn"
              >
                {loading ? "Signing in…" : "Log in"}
                {!loading && <ArrowRight className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5" />}
              </Button>
            </form>

            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-5">
              Don&apos;t have an account?{" "}
              <Link to="/signup" className="font-bold text-[#2A7B6F] dark:text-teal-400" data-testid="signup-link">Sign up</Link>
            </p>

            {/* Privacy badge */}
            <div className="mt-6 flex items-center gap-4 p-4 rounded-2xl bg-[#EBF5F2] dark:bg-teal-950/40">
              <div
                className="flex items-center justify-center shrink-0 rounded-full bg-teal-600/[.12] dark:bg-teal-400/20"
                style={{ width: 44, height: 44 }}
              >
                <ShieldCheck className="text-[#2A7B6F] dark:text-teal-400" style={{ width: 22, height: 22 }} />
              </div>
              <div>
                <p className="text-sm font-bold text-[#2A7B6F] dark:text-teal-400">Your data is safe with us</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mt-0.5">
                  We never share your information with third parties.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── DESKTOP — two equal halves: quote | form card ───────── */}
      <div
        className={`hidden md:flex min-h-screen transition-opacity duration-300 bg-[#EBF5F2] dark:bg-[#091714] ${revealed ? "opacity-100" : "opacity-0"}`}
        data-kp-auth-page="true"
      >
        {/* Left half: quote */}
        <div className="w-1/2 flex flex-col px-16 xl:px-24 py-12">
          <Link to="/" className="inline-flex items-center gap-2 shrink-0">
            <img src={LOGO} alt="Kiddo+" className="w-10 h-10 rounded-[22%]" />
            <img src={WORDMARK} alt="Kiddo+" className="h-8 w-auto dark:brightness-0 dark:invert" />
          </Link>

          <div className="flex-1 flex flex-col justify-center">
            <span
              className="block leading-none mb-5 select-none text-[#2A7B6F] dark:text-teal-400"
              style={{ fontSize: "5rem", fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 0.8 }}
            >❝</span>
            <h1
              className="font-black leading-tight tracking-tight mb-5 text-[#1A2E2A] dark:text-[#DFF2EE]"
              style={{ fontSize: "clamp(2rem, 3.2vw, 3rem)" }}
            >
              {quote.headline.split("\n").map((line, li, arr) => (
                <span key={li}>
                  {parseHeadline(line).map((part, pi) =>
                    part.teal
                      ? <span key={pi} className="text-[#2A7B6F] dark:text-teal-400">{part.text}</span>
                      : part.text
                  )}
                  {li < arr.length - 1 && <br />}
                </span>
              ))}
            </h1>
            <div className="rounded-full mb-5 bg-[#2A7B6F] dark:bg-teal-400" style={{ width: 52, height: 3 }} />
            {(() => {
              const parts = quote.tagline.split("\n");
              return parts.length >= 2
                ? <p className="text-lg font-medium leading-relaxed text-[#2A7B6F] dark:text-teal-400">{parts[0]} {parts[1]}</p>
                : <p className="text-lg font-medium text-[#2A7B6F] dark:text-teal-400">{parts[0]}</p>;
            })()}
          </div>
        </div>

        {/* Right half: floating card */}
        <div className="w-1/2 flex items-center justify-center px-8 xl:px-10 py-12">
          <div
            className="w-full rounded-3xl bg-white dark:bg-kp-surface p-10 xl:p-12 auth-desktop-card"
            style={{ maxWidth: 680 }}
          >
            <h2 className="text-2xl font-black mb-1 text-[#1A2E2A] dark:text-[#DFF2EE]">Welcome back</h2>
            <p className="text-sm mb-6 text-gray-500 dark:text-gray-400">Sign in to continue your journey.</p>

            {/* DISA banner */}
            {disaMessage && (
              <div
                className="mb-5 flex items-start gap-2.5 p-3 rounded-2xl text-sm bg-[#EBF5F2] dark:bg-teal-950/40 border border-[#B2D8D0] dark:border-teal-700/40 text-[#1A5C52] dark:text-teal-300"
                data-testid="disa-login-message"
              >
                <span className="mt-0.5 text-[#2A7B6F] dark:text-teal-400">✦</span>
                <span>{disaMessage}{" "}<Link to="/signup" className="font-bold underline">Create a free account</Link>{" "}or log in below.</span>
              </div>
            )}

            {/* Google */}
            <GoogleSignInButton
              label="Continue with Google"
              className="!rounded-full !py-[0.9rem] mb-5 !bg-[#ECEDF8] dark:!bg-[#1A2535]"
              style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.09), 0 1px 2px rgba(0,0,0,0.06)" }}
            />

            {/* "or" divider */}
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
              <span className="text-sm text-gray-400 dark:text-gray-500">or</span>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            </div>

            {/* Form */}
            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              {/* Email */}
              <div>
                <Label htmlFor="d-email" className="block text-sm font-bold mb-2 text-[#1A2E2A] dark:text-[#DFF2EE]">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" style={{ width: 18, height: 18 }} />
                  <Input
                    id="d-email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-auto text-sm placeholder:text-gray-400 focus-visible:ring-offset-0"
                    style={{ borderRadius: "9999px", paddingTop: "0.85rem", paddingBottom: "0.85rem", paddingLeft: "3rem", paddingRight: "1rem" }}
                    data-testid="login-email-input-desktop"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="d-pwd" className="text-sm font-bold text-[#1A2E2A] dark:text-[#DFF2EE]">Password</Label>
                  <button
                    type="button"
                    onClick={() => setForgotOpen(true)}
                    className="text-sm font-medium text-[#2A7B6F] dark:text-teal-400"
                    data-testid="forgot-password-link-desktop"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10 text-gray-400" style={{ width: 18, height: 18 }} />
                  <PasswordInput
                    id="d-pwd"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="h-auto text-sm placeholder:text-gray-400 focus-visible:ring-offset-0"
                    style={{ borderRadius: "9999px", paddingTop: "0.85rem", paddingBottom: "0.85rem", paddingLeft: "3rem" }}
                    data-testid="login-password-input-desktop"
                  />
                </div>
              </div>

              {/* Remember me */}
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-5 h-5 rounded cursor-pointer"
                  style={{ accentColor: "#2A7B6F" }}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">Remember me</span>
              </label>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="relative h-auto w-full text-white rounded-full font-bold text-base transition-colors mt-1"
                style={{ background: loading ? "#4A9E8F" : "#2A7B6F", paddingTop: "0.9rem", paddingBottom: "0.9rem" }}
                data-testid="login-submit-btn-desktop"
              >
                {loading ? "Signing in…" : "Log in"}
                {!loading && <ArrowRight className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5" />}
              </Button>
            </form>

            {/* Sign up link */}
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-5">
              Don&apos;t have an account?{" "}
              <Link to="/signup" className="font-bold text-[#2A7B6F] dark:text-teal-400" data-testid="signup-link-desktop">Sign up</Link>
            </p>

            {/* Privacy badge */}
            <div className="mt-5 flex items-center gap-3 p-3.5 rounded-2xl bg-[#EBF5F2] dark:bg-teal-950/40">
              <div
                className="flex items-center justify-center shrink-0 rounded-full bg-teal-600/[.12] dark:bg-teal-400/20"
                style={{ width: 38, height: 38 }}
              >
                <ShieldCheck className="text-[#2A7B6F] dark:text-teal-400" style={{ width: 19, height: 19 }} />
              </div>
              <div>
                <p className="text-xs font-bold text-[#2A7B6F] dark:text-teal-400">Your data is safe with us</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">We never share your information.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} prefillEmail={email} />
    </>
  );
}

function ForgotPasswordDialog({ open, onOpenChange, prefillEmail }) {
  const [email, setEmail] = useState(prefillEmail || "");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.forgotPassword(email);
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => { setSent(false); setEmail(prefillEmail || ""); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => o ? onOpenChange(true) : close()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="forgot-password-dialog">
        {!sent ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl dark:text-[#DFF2EE]">Forgot your password?</DialogTitle>
              <DialogDescription className="dark:text-gray-400">
                Enter the email you signed up with. We&apos;ll send a one-time reset link.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="fp-email" className="text-sm font-bold text-gray-900 dark:text-[#DFF2EE]">Email</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400 pointer-events-none" />
                  <Input
                    id="fp-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="Enter your email"
                    className="h-auto rounded-full py-3.5 pl-12 pr-4 text-sm placeholder:text-gray-400 focus-visible:ring-teal-500/20 focus-visible:border-teal-500/70 focus-visible:ring-offset-0"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full bg-teal-600 hover:bg-teal-700 active:bg-teal-800 text-white rounded-full py-3.5 font-bold"
                data-testid="forgot-submit-btn"
              >
                <Mail className="w-4 h-4 mr-1" /> {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-teal-50 dark:bg-teal-950/40 flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-teal-600 dark:text-teal-400" />
            </div>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-center dark:text-[#DFF2EE]">Check your inbox</DialogTitle>
              <DialogDescription className="text-center dark:text-gray-400">
                If an account exists for <strong>{email}</strong>, a reset link is on its way. Expires in 1 hour.
              </DialogDescription>
            </DialogHeader>
            <p className="text-xs text-inkmid dark:text-gray-400 mt-4">Didn&apos;t get it? Check your spam folder or try again.</p>
            <Button
              onClick={close}
              className="mt-4 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-bold px-8"
              data-testid="forgot-close-btn"
            >
              Got it
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
